import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Container, Form, Header, Segment, Loader, Message } from 'semantic-ui-react';
import { useToast } from './utils/Toast';
import { useTheme } from './ThemeContext';
import { supabase } from './supabaseClient';
import './AuthPages.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code') ?? searchParams.get('token');
  const { showToast } = useToast();
  const theme = useTheme();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [exchangeStatus, setExchangeStatus] = useState<'pending' | 'error' | 'ready'>(code ? 'pending' : 'error');
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  const logoSrc = theme === 'dark' 
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  useEffect(() => {
    const exchange = async () => {
      if (!code) {
        setExchangeStatus('error');
        setExchangeError('Invalid or missing reset code.');
        return;
      }

      const { error } = await supabase.auth.exchangeCodeForSession(code);

      if (error) {
        setExchangeStatus('error');
        setExchangeError(error.message);
        return;
      }

      setExchangeStatus('ready');
    };

    exchange().catch((err) => {
      console.error('Failed to exchange recovery code:', err);
      setExchangeStatus('error');
      setExchangeError('Unable to verify reset link. Please request a new one.');
    });
  }, [code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  setLoading(true);

  if (exchangeStatus !== 'ready') {
      showToast('Reset link is not valid. Please request a new one.', 'error');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      showToast('Password must be at least 8 characters long', 'warning');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        throw error;
      }

      showToast('Password reset successful!', 'success');
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to reset password right now.';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (exchangeStatus === 'pending') {
    return (
      <div className="auth-container">
        <Container text>
          <div className="auth-card">
            <div className="auth-logo-container">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
            </div>
            
            <Segment raised className="auth-segment">
              <Loader active inline="centered" content="Verifying reset link..." />
            </Segment>
          </div>
        </Container>
      </div>
    );
  }

  if (exchangeStatus === 'error') {
    return (
      <div className="auth-container">
        <Container text>
          <div className="auth-card">
            <div className="auth-logo-container">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
            </div>
            
            <Segment raised className="auth-segment">
              <Message warning>
                <Message.Header>Unable to continue</Message.Header>
                <p>{exchangeError || 'Invalid or missing reset token.'}</p>
              </Message>

              <div className="auth-links">
                <Link to="/" className="auth-link">
                  Back to Sign In
                </Link>
              </div>
            </Segment>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <Container text>
        <div className="auth-card">
          <div className="auth-logo-container">
            <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
          </div>
          <Header as="h2" textAlign="center" className="auth-subheader">
            Create New Password
          </Header>
          
          <Segment raised className="auth-segment">
            <Form onSubmit={handleSubmit}>
              <Form.Field>
                <label>New Password</label>
                <Form.Input
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  icon="lock"
                  iconPosition="left"
                />
              </Form.Field>

              <Form.Field>
                <label>Confirm Password</label>
                <Form.Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  icon="lock"
                  iconPosition="left"
                />
              </Form.Field>

              <Button
                type="submit"
                primary
                fluid
                size="large"
                loading={loading}
                disabled={loading}
                className="auth-button"
              >
                Reset Password
              </Button>
            </Form>

            <div className="auth-links">
              <Link to="/" className="auth-link">
                Back to Sign In
              </Link>
            </div>
          </Segment>
        </div>
      </Container>
    </div>
  );
};

export default ResetPassword;
